import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Trophy, TrendingUp, MousePointer, Users, Award, Sparkles,
  Calendar, ExternalLink, Copy, Check, ArrowRight, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BoostDashboardPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    const savedEmail = localStorage.getItem('boost_email');
    if (savedEmail && !email) {
      setEmail(savedEmail);
      fetchStats(savedEmail);
    } else if (email) {
      fetchStats(email);
    }
  }, []);

  const fetchStats = async (userEmail) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/boost/my-stats?email=${encodeURIComponent(userEmail)}`);
      setStats(response.data);
      localStorage.setItem('boost_email', userEmail);
    } catch (error) {
      toast.error('Failed to load stats');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      fetchStats(email);
    }
  };

  const copyReferralLink = async (referral) => {
    const url = `https://events.jigsawconferences.co.uk/events?ref=${referral.referral_code}`;
    await navigator.clipboard.writeText(url);
    setCopiedCode(referral.referral_code);
    toast.success('Link copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <MainLayout>
      <SEOHead
        title="My Referral Dashboard | Jigsaw Events Hub"
        description="Track your event referrals, see your rewards, and climb the leaderboard."
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Event Boost Program</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Your Referral Dashboard</h1>
            <p className="text-xl text-orange-100">Track your impact and earn rewards for spreading the word</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 -mt-8">
          {/* Email Lookup Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
            <form onSubmit={handleSubmit} className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Your Email Address</label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={loading || !email}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'View Stats'}
                </Button>
              </div>
            </form>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          )}

          {stats && !loading && (
            <>
              {/* Stats Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-xl shadow-md p-5">
                  <div className="flex items-center gap-2 text-gray-500 mb-2">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">Events Promoted</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_events_promoted}</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-5">
                  <div className="flex items-center gap-2 text-gray-500 mb-2">
                    <MousePointer className="w-4 h-4" />
                    <span className="text-sm">Total Clicks</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_clicks}</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-5">
                  <div className="flex items-center gap-2 text-gray-500 mb-2">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">Total Referrals</span>
                  </div>
                  <p className="text-3xl font-bold text-orange-600">{stats.total_conversions}</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-5">
                  <div className="flex items-center gap-2 text-gray-500 mb-2">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm">Conversion Rate</span>
                  </div>
                  <p className="text-3xl font-bold text-green-600">{stats.overall_conversion_rate}%</p>
                </div>
              </div>

              {/* Rewards Earned */}
              {stats.rewards_earned && stats.rewards_earned.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6 mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Trophy className="w-6 h-6 text-amber-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Rewards Earned</h2>
                  </div>
                  <div className="grid gap-3">
                    {stats.rewards_earned.map((reward, index) => (
                      <div key={index} className="flex items-center justify-between bg-white rounded-lg p-4 border border-amber-100">
                        <div>
                          <p className="font-medium text-gray-900">{reward.reward}</p>
                          <p className="text-sm text-gray-500">{reward.event_name}</p>
                        </div>
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                          {reward.tier}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Referral Links */}
              <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-8">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900">Your Referral Links</h2>
                  <p className="text-gray-500 text-sm mt-1">Share these links to earn referrals</p>
                </div>
                
                {stats.referrals && stats.referrals.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {stats.referrals.map((referral) => (
                      <div key={referral.id} className="p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{referral.event_name}</h3>
                            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <MousePointer className="w-3 h-3" />
                                {referral.clicks} clicks
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {referral.registrations} referrals
                              </span>
                              <span className="flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" />
                                {referral.clicks > 0 ? Math.round((referral.registrations / referral.clicks) * 100) : 0}% conversion
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => copyReferralLink(referral)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Copy link"
                            >
                              {copiedCode === referral.referral_code ? (
                                <Check className="w-5 h-5 text-green-600" />
                              ) : (
                                <Copy className="w-5 h-5 text-gray-400" />
                              )}
                            </button>
                            <a
                              href={`/events?ref=${referral.referral_code}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="View event"
                            >
                              <ExternalLink className="w-5 h-5 text-gray-400" />
                            </a>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Progress to next reward</span>
                            <span>{referral.registrations}/5 referrals</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all"
                              style={{ width: `${Math.min((referral.registrations / 5) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Award className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No referrals yet</h3>
                    <p className="text-gray-500 mb-6">Start promoting events to earn rewards!</p>
                    <Link to="/events">
                      <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600">
                        Browse Events <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                )}
              </div>

              {/* CTA */}
              <div className="text-center pb-12">
                <Link to="/events">
                  <Button variant="outline" size="lg">
                    Find More Events to Promote <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </>
          )}

          {!stats && !loading && (
            <div className="text-center py-12">
              <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Enter your email to view stats</h2>
              <p className="text-gray-500">See your referral performance and earned rewards</p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}